import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../../lib/api.js';
import { toast } from '../../components/ui/Toast.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { Badge } from '../../components/ui/Badge.js';
import { Spinner } from '../../components/ui/Spinner.js';
import type { Invite } from '@ember/types';

export default function AdminInvitesPage() {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [creating, setCreating] = useState(false);

  function load() {
    setLoading(true);
    api
      .get<{ invites: Invite[] }>('/api/admin/invites')
      .then((r) => setInvites(r.invites))
      .catch(() => toast.error('Failed to load invites'))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setCreating(true);
    try {
      // POST /api/admin/invites — body: { email }
      // Creates invite and sends invite email to the address
      await api.post('/api/admin/invites', { email });
      toast.success(`Invite sent to ${email}`);
      setNewEmail('');
      load();
    } catch {
      toast.error('Failed to send invite');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(token: string) {
    if (!confirm('Revoke this invite?')) return;
    try {
      await api.delete(`/api/admin/invites/${token}`);
      toast.success('Invite revoked');
      load();
    } catch {
      toast.error('Failed to revoke invite');
    }
  }

  const unused = invites.filter((i) => !i.usedAt);
  const used = invites.filter((i) => i.usedAt);

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold text-neutral-100 mb-2">Invite Links</h1>
      <p className="text-sm text-neutral-400 mb-6">
        Enter an email address to generate an invite link and send it directly to the recipient.
      </p>

      {/* Create invite */}
      <form onSubmit={handleCreate} className="flex gap-2 mb-6">
        <Input
          type="email"
          placeholder="user@example.com"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          className="flex-1"
        />
        <Button type="submit" isLoading={creating}>
          Send invite
        </Button>
      </form>

      {loading ? (
        <Spinner />
      ) : (
        <>
          {unused.length > 0 && (
            <div className="mb-6">
              <h2 className="text-sm font-semibold text-neutral-400 mb-2 uppercase tracking-wide">
                Pending ({unused.length})
              </h2>
              <ul className="divide-y divide-neutral-800 rounded-xl border border-neutral-800 bg-neutral-900 overflow-hidden">
                {unused.map((inv) => (
                  <li key={inv.token} className="flex items-center justify-between px-4 py-3 gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-neutral-200 truncate">{inv.email}</p>
                      <p className="text-xs text-neutral-600">
                        Sent {new Date(inv.createdAt).toLocaleDateString()}
                        {inv.expiresAt && ` · Expires ${new Date(inv.expiresAt).toLocaleDateString()}`}
                      </p>
                    </div>
                    <Button size="sm" variant="danger" onClick={() => void handleRevoke(inv.token)}>
                      Revoke
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {used.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-neutral-400 mb-2 uppercase tracking-wide">
                Used ({used.length})
              </h2>
              <ul className="divide-y divide-neutral-800 rounded-xl border border-neutral-800 bg-neutral-900 overflow-hidden">
                {used.map((inv) => (
                  <li key={inv.token} className="flex items-center justify-between px-4 py-3 gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-neutral-600 truncate">{inv.email}</p>
                      <p className="text-xs text-neutral-600">
                        Used {inv.usedAt ? new Date(inv.usedAt).toLocaleDateString() : ''}
                      </p>
                    </div>
                    <Badge variant="default">Used</Badge>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {invites.length === 0 && (
            <p className="text-neutral-500 text-sm">No invites yet.</p>
          )}
        </>
      )}
    </div>
  );
}
